import { of, tap, throwError, timer } from 'rxjs';
import { catchError, shareReplay, timeout } from 'rxjs/operators';
import { CustomPDFReportType } from '../enums/custom-pdf-report-type.enum';
import { CustomPDFReportOrientation } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-orientation.enum';
import { ReportNamingConvention } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/enums/naming-convention.enum';
import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { formFlattenedParamString } from 'Angular/utils/urls';
import * as i0 from "@angular/core";
import * as i1 from "Angular/services/core/ajax.service";
import * as i2 from "Angular/services/core/urls.service";
export class CustomPDFReportModulePreviewImageService {
    constructor(ajaxService, urlsService) {
        this.ajaxService = ajaxService;
        this.urlsService = urlsService;
        this.cache = {};
        // 3 minutes in milliseconds in case preview images contain intraday data
        this.ttl = 3 * 60 * 1000;
    }
    getPDFReportModulePreviewImage(reportType, params) {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.PREVIEW_IMAGE, {
            reportType,
        });
        // Combinations of module type, preview securities, naming convention, custom labels, data source,
        // language, and field values will have the same preview image for a user until the TTL expires.
        const cacheKey = `${params.module_type}_${params.naming_convention ?? ReportNamingConvention.TRUNCATED}
            _${(params.custom_labels ?? []).join(',')}_${params.security_ids.join(',')}_${params.data_source ?? ''}
            _${params.language ?? ''}_${formFlattenedParamString(params.customizable_fields, Object.keys(params.customizable_fields).sort())}`;
        // If we have image data in the cache then return it
        if (this.cache[cacheKey]?.data.imageUrls.length) {
            return of(this.cache[cacheKey].data);
        }
        // If a request is already in progress for the cache key then return the observable
        if (this.cache[cacheKey]?.observable) {
            return this.cache[cacheKey].observable;
        }
        // tap/catchError sit upstream of shareReplay so they still run if the caller unsubscribed
        // before the response arrived; downstream, a dead subscriber would skip TTL eviction and
        // pin the value in the replay buffer forever.
        //
        // timeout() sits upstream of catchError too -- without it, a genuinely stalled request
        // (no HTTP response, no error) never resolves, shareReplay(1) keeps the source subscribed
        // forever, and every future Preview click for the same params replays the same hung
        // observable. Callers without their own deadline (module editor, talking-point warning
        // check) must tolerate a TimeoutError here, which lacks the HttpErrorResponse shape a
        // normal failure has.
        const observable = this.ajaxService
            .postJsonDataObserveBody(url, params)
            .pipe(timeout(30000), tap((response) => {
            this.cache[cacheKey] = {
                data: response,
                observable: null,
            };
            // Evict from image cache after the TTL has passed
            timer(this.ttl).subscribe(() => {
                delete this.cache[cacheKey];
            });
        }), catchError((error) => {
            // Otherwise shareReplay(1) would keep replaying this error to every future call.
            delete this.cache[cacheKey];
            return throwError(() => error);
        }), shareReplay(1));
        this.cache[cacheKey] = {
            data: { imageUrls: [], warning: '' },
            observable,
        };
        return observable;
    }
    // Only resolves visibility/position, never renders an image, so there's nothing to cache here.
    getPDFReportSectionsPreviewImages(reportType, reportId, sections, params) {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.PREVIEW_IMAGE, {
            reportType,
        });
        return this.ajaxService.postJsonDataObserveBody(url, {
            report_id: reportId,
            sections,
            ...params,
        });
    }
    getPDFReportModuleProposalsWarningMessage(talkingPoint, reportOrientation = CustomPDFReportOrientation.PORTRAIT, reportId = null) {
        return this.getPDFReportModulePreviewImage(CustomPDFReportType.AGGREGATE_SECURITY_OVERVIEW, {
            security_ids: [],
            report_id: reportId,
            customizable_fields: {
                select_talking_points: true,
                module_title: 'Proposal',
                talking_points: [talkingPoint],
            },
            naming_convention: ReportNamingConvention.TRUNCATED,
            custom_labels: [],
            module_type: 'HALF_PAGE_PROPOSAL_PORTRAIT_MODULE',
            report_orientation: reportOrientation,
        });
    }
    getTalkingPointPreviewImage(securityIds, talkingPoint, includeBenchmark) {
        const params = {
            security_ids: securityIds.filter((_) => _),
            talking_point: talkingPoint,
            include_benchmark: includeBenchmark,
        };
        return this.ajaxService.postJsonDataObserveBody(this.urlsService.CUSTOM_PDF_REPORTS.TALKING_POINT_PREVIEW_IMAGE, params);
    }
}
CustomPDFReportModulePreviewImageService.ɵfac = function CustomPDFReportModulePreviewImageService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || CustomPDFReportModulePreviewImageService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.UrlsService)); };
CustomPDFReportModulePreviewImageService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CustomPDFReportModulePreviewImageService, factory: CustomPDFReportModulePreviewImageService.ɵfac, providedIn: 'root' });
